import { useEffect, useState } from "react";
import { Check, HeartHandshake, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { APOIO_CATEGORY_LABELS, formatApiError, formatDate } from "@/lib/api";
import { Loading, Pill, StateBlock } from "@/components/common";
import { Avatar } from "@/components/Avatar";
import { BiometricLockCard } from "@/components/BiometricLockCard";
import { ActionButton, Card, CardMeta, CardTitle, PageHero, Section } from "./PageShell";
import { useSelectedPatient } from "./patientContext";

const CARE_TEAM_ROLES = [
  { key: "counselor", label: "Conselheiro" },
  { key: "psychologist", label: "Psicólogo(a)" },
  { key: "psychiatrist", label: "Psiquiatra" },
];

/** Somente leitura: a equipe é definida no Portal Erimus e acompanha o paciente selecionado. */
const CareTeamSection = ({ patient }) => (
  <Section label="Equipe responsável" testId="apoio-care-team-section">
    <Card testId="apoio-care-team-card">
      <div className="flex items-start gap-3.5">
        <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-sage-50 text-sage-700">
          <Stethoscope size={17} aria-hidden="true" />
        </span>
        <div className="flex-1">
          <CardTitle>{patient.patient_name}</CardTitle>
          <dl className="mt-3 space-y-2.5">
            {CARE_TEAM_ROLES.map(({ key, label }) => {
              const person = patient.care_team?.[key];
              return (
                <div key={key} className="flex flex-wrap items-center justify-between gap-2">
                  <dt className="text-[13px] font-semibold uppercase tracking-[0.08em] text-ink-500">{label}</dt>
                  <dd
                    data-testid={`apoio-care-team-${key}`}
                    className={`flex items-center gap-2.5 text-[15px] ${
                      person ? "font-medium text-ink-800" : "text-ink-500"
                    }`}
                  >
                    {person ? (
                      <Avatar
                        name={person.name}
                        photoUrl={person.photo_thumb_url}
                        size={32}
                        testId={`apoio-care-team-avatar-${key}`}
                      />
                    ) : null}
                    {person?.name || "Não definido"}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      </div>
    </Card>
  </Section>
);

const PatientCard = ({ patient, selected, onSelect, index }) => (
  <Card
    testId={`apoio-patient-${patient.patient_id}`}
    tone={selected ? "highlight" : "default"}
    className="er-rise"
    style={{ animationDelay: `${index * 70}ms` }}
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <CardTitle>{patient.patient_name}</CardTitle>
        <p className="mt-1 text-[14px] text-ink-600">{patient.relationship}</p>
      </div>
      {selected ? (
        <Pill tone="success" testId={`apoio-patient-selected-${patient.patient_id}`}>
          <Check size={12} className="mr-1 inline" aria-hidden="true" />
          Selecionado
        </Pill>
      ) : null}
    </div>

    <div className="mt-3.5 flex flex-wrap gap-2">
      {(patient.categories || []).map((c) => (
        <Pill key={c}>{APOIO_CATEGORY_LABELS[c] || c}</Pill>
      ))}
    </div>

    {!selected ? (
      <ActionButton
        variant="secondary"
        data-testid={`apoio-patient-select-${patient.patient_id}`}
        onClick={() => onSelect(patient.patient_id)}
        className="mt-5 w-full"
      >
        Selecionar este paciente
      </ActionButton>
    ) : null}
  </Card>
);

export default function ApoioPatients() {
  const { api, forceSignOut } = useAuth();
  const { patients, selectedPatientId, selectedPatient, selectPatient, epoch, error, getSignal, handleAccessLost } =
    useSelectedPatient();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    setSummary(null);
    if (!selectedPatientId || !selectedPatient?.can_view_summary) return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/network/patients/${selectedPatientId}/summary`, { signal: getSignal() });
        if (active) setSummary(data);
      } catch (e) {
        if (e.response?.status === 401) forceSignOut();
        if (e.response?.status === 403) handleAccessLost();
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatientId, epoch, selectedPatient?.can_view_summary]);

  const onSelect = (id) => {
    const target = patients.find((p) => p.patient_id === id);
    selectPatient(id);
    toast.success(`Paciente alterado para ${target?.patient_name || "outro paciente"}`);
  };

  if (patients === null) return <Loading label="Carregando pessoas autorizadas…" />;

  return (
    <div data-testid="apoio-patients-page" className="space-y-8">
      <PageHero
        testId="apoio-patients-hero"
        kicker="Acompanhamento autorizado"
        title="Paciente"
        subtitle="Boletins, medicamentos e alertas sempre mostram o paciente selecionado abaixo."
        banner="paciente"
      />

      {error ? (
        <p data-testid="apoio-patients-error" className="rounded-2xl bg-danger/10 px-4 py-3 text-[14px] text-danger">
          {error}
        </p>
      ) : null}

      {selectedPatient ? <CareTeamSection patient={selectedPatient} /> : null}

      {summary?.episode ? (
        <Section label="Resumo do tratamento" testId="apoio-summary-section">
          <Card>
            <CardTitle>{summary.episode.title}</CardTitle>
            <p className="mt-2.5 text-[15px] leading-relaxed text-ink-600">{summary.episode.summary}</p>
            <CardMeta>
              <span className="mt-3 block">Acompanhamento desde {formatDate(summary.episode.started_at)}</span>
            </CardMeta>
          </Card>
        </Section>
      ) : null}

      <Section label="Pessoas que você acompanha">
        {patients.length === 0 ? (
          <StateBlock
            testId="apoio-patients-empty"
            icon={HeartHandshake}
            title="Nenhum paciente disponível"
            description="Quando a equipe clínica autorizar um vínculo de acompanhamento, ele aparecerá aqui."
          />
        ) : (
          patients.map((p, i) => (
            <PatientCard
              key={p.patient_id}
              patient={p}
              index={i}
              selected={p.patient_id === selectedPatientId}
              onSelect={onSelect}
            />
          ))
        )}
      </Section>

      <BiometricLockCard productName="Erimus – Rede de Apoio" />
    </div>
  );
}
